import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';


function toIso(date) {
  return date instanceof Date ? date.toISOString() : date;
}

function getAuthHeaders() {
  return {};
}

export function useAdminAnalytics(fromUtc, toUtc) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch_ = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromUtc) params.set('from_utc', toIso(fromUtc));
      if (toUtc) params.set('to_utc', toIso(toUtc));
      const res = await api.get(`/admin/analytics/overview?${params}`);

      const json = res.data;
      if (!ctrl.signal.aborted) setData(json.data);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [fromUtc, toUtc]);

  useEffect(() => { fetch_(); return () => abortRef.current?.abort(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}

export function useTopClinics(fromUtc, toUtc, metric = 'commission_net', limit = 20) {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ metric, limit: String(limit) });
      if (fromUtc) params.set('from_utc', toIso(fromUtc));
      if (toUtc) params.set('to_utc', toIso(toUtc));
      const res = await api.get(`/admin/analytics/clinics/top?${params}`);
      if (res.status === 503 || res.status === 502) { setClinics([]); return; }

      const json = res.data;
      setClinics(json.clinics || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fromUtc, toUtc, metric, limit]);

  useEffect(() => { fetch_(); }, [fetch_]);
  return { clinics, loading, error, refetch: fetch_ };
}

export function useFinanceHeartbeat(fromUtc, toUtc, autoRefreshMs = 30000) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetch_ = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (fromUtc) params.set('from_utc', toIso(fromUtc));
      if (toUtc) params.set('to_utc', toIso(toUtc));
      const res = await api.get(`/admin/finance/dashboard?${params}`);
      if (res.status === 503 || res.status === 502) { setData(null); setLoading(false); return; }

      const json = res.data;
      setData(json.dashboard || json.data || json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fromUtc, toUtc]);

  useEffect(() => {
    fetch_();
    if (autoRefreshMs > 0) {
      intervalRef.current = setInterval(fetch_, autoRefreshMs);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetch_, autoRefreshMs]);

  return { data, loading, error, refetch: fetch_ };
}


export function useAttribution(fromUtc, toUtc, groupBy = 'utm_campaign') {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch_ = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ group_by: groupBy });
      if (fromUtc) params.set('from_utc', toIso(fromUtc));
      if (toUtc) params.set('to_utc', toIso(toUtc));
      const res = await api.get(`/admin/analytics/attribution?${params}`);
      if (res.status === 503 || res.status === 502) { if (!ctrl.signal.aborted) { setData(null); setLoading(false); } return; }

      const json = res.data;
      if (!ctrl.signal.aborted) setData(json);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [fromUtc, toUtc, groupBy]);

  useEffect(() => { fetch_(); return () => abortRef.current?.abort(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}

export function useCampaignROI(fromUtc, toUtc) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch_ = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromUtc) params.set('from_utc', toIso(fromUtc));
      if (toUtc) params.set('to_utc', toIso(toUtc));
      const res = await api.get(`/admin/analytics/campaign-roi?${params}`);
      if (res.status === 503 || res.status === 502) { if (!ctrl.signal.aborted) { setData(null); setLoading(false); } return; }

      const json = res.data;
      if (!ctrl.signal.aborted) setData(json);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [fromUtc, toUtc]);

  useEffect(() => { fetch_(); return () => abortRef.current?.abort(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}

export function useForecast(windowDays = 30) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/analytics/forecast?window_days=${windowDays}`);
      if (res.status === 503 || res.status === 502) { setData(null); setLoading(false); return; }

      const json = res.data;
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { fetch_(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}


export function useOfferAnalytics(fromUtc, toUtc) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch_ = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromUtc) params.set('from_utc', toIso(fromUtc));
      if (toUtc) params.set('to_utc', toIso(toUtc));
      const token = '';
      const headers = {};
      const res = await api.get(`/admin/analytics/offers?${params}`);
      if (res.status === 503 || res.status === 502) { if (!ctrl.signal.aborted) { setData(null); setLoading(false); } return; }

      const json = res.data;
      if (!ctrl.signal.aborted) setData(json.data);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [fromUtc, toUtc]);

  useEffect(() => { fetch_(); return () => abortRef.current?.abort(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}


export function useResponseSpeed() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch_ = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const token = '';
      const headers = {};
      const res = await api.get(`/admin/analytics/response-speed`);
      if (res.status === 503 || res.status === 502) { if (!ctrl.signal.aborted) { setData(null); setLoading(false); } return; }

      const json = res.data;
      if (!ctrl.signal.aborted) setData(json);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => { fetch_(); return () => abortRef.current?.abort(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}

export function useMessagingFunnel(fromUtc, toUtc) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch_ = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const headers = getAuthHeaders();
      const res = await api.get(`/admin/analytics/messaging-funnel`);
      if (res.status === 503 || res.status === 502) { if (!ctrl.signal.aborted) { setData(null); setLoading(false); } return; }

      const json = res.data;
      if (!ctrl.signal.aborted) setData(json);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [fromUtc, toUtc]);

  useEffect(() => { fetch_(); return () => abortRef.current?.abort(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}
